import { FC, FormEvent, useEffect, useState, useRef } from 'react';
import { Box } from 'ui/Box';
import { Typography } from 'ui/Typography';
import { Button } from 'components/Button';
import { PlusIcon } from 'components/icons/PlusIcon';
import { Modal } from 'ui/Modal';
import { ModalMemberItem } from '../ModalMemberItem';
import { Member, UserRole, AddedMember } from 'types/api.d';
import * as Yup from 'yup';
import { useAddCompanyMembers } from 'modules/RegisterModule/hooks/useAddCompanyMembers';
import { useEditCompanyMember } from 'modules/RegisterModule/hooks/useEditCompanyMember';
import { useDeleteCompanyMember } from 'modules/RegisterModule/hooks/useDeleteCompanyMember';
import { memberSchema } from 'modules/RegisterModule/helpers';
import { AddedMemberItem } from '../AddedMemberItem';
import { DeleteModal } from 'components/DeleteModal';

export interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  nextStep: () => void;
}

const selectOptions = [
  { value: UserRole.SA, label: 'Super admin' },
  { value: UserRole.MAN, label: 'Manager' },
  { value: UserRole.MEM, label: 'Team Member' },
];

export const AddMemberModal: FC<AddMemberModalProps> = ({
  open,
  onClose,
  nextStep,
}) => {
  const [addedMembers, setAddedMembers] = useState<Array<AddedMember>>([]);
  const [editMember, setEditMember] = useState<AddedMember | null>(null);
  const [errors, setErrors] = useState<Member>({} as Member);
  const [resetForm, setResetForm] = useState(false);

  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteMemberId, setDeleteMemberId] = useState<number | null>(null);
  const [isAddButtonDisabled, setAddButtonDisabled] = useState(true);
  const [shouldFormClose, setShouldFormClose] = useState(false);
  const formReference = useRef<HTMLFormElement>(null);
  const {
    member,
    status,
    errors: addErrors,
    addCompanyMember,
  } = useAddCompanyMembers();
  const {
    member: editedSavedMember,
    status: statusEdit,
    errors: errorsEdit,
    editCompanyMember,
  } = useEditCompanyMember();

  const { deleteMember, status: deleteStatus } = useDeleteCompanyMember();

  useEffect(() => {
    if (status === 'success') {
      setResetForm(true);
      setAddButtonDisabled(true);
      setAddedMembers([...addedMembers, member as AddedMember]);
      if (shouldFormClose) {
        nextStep();
        onClose();
      }
    }
    if (status === 'failed') {
      setErrors(addErrors as Member);
    }
  }, [status]);

  useEffect(() => {
    if (statusEdit === 'success') {
      setEditMember(null);
      setResetForm(true);
      const mappedMembers = addedMembers.map((member) =>
        member.id === editedSavedMember?.id ? editedSavedMember : member
      );
      setAddedMembers(mappedMembers);
    }
    if (statusEdit === 'failed') {
      setErrors(errorsEdit as Member);
    }
  }, [statusEdit]);

  useEffect(() => {
    if (deleteStatus === 'success') {
      setAddedMembers(
        addedMembers.filter((member) => member.id !== deleteMemberId)
      );
    }
  }, [deleteStatus]);

  const onDelete = (id: number) => {
    setConfirmModalOpen(true);
    setDeleteMemberId(id);
  };

  const onConfirmDelete = () => {
    if (deleteMemberId) {
      deleteMember(deleteMemberId);
      setConfirmModalOpen(false);
    }
  };

  const onFormSubmit = async (
    event?: FormEvent<HTMLFormElement>,
    onSaveData?: FormData
  ) => {
    setResetForm(false);
    event?.preventDefault();
    const formData = event ? new FormData(event.currentTarget) : onSaveData!;
    const data = Object.fromEntries(formData.entries()) as unknown as Member;
    setErrors({} as Member);

    try {
      await memberSchema.validate(data, { abortEarly: false });
      if (
        addedMembers.some(
          (member) =>
            member.email === data.email && member.email !== editMember?.email
        )
      ) {
        setErrors((prev) => ({ ...prev, email: 'Email already exists' }));
        return;
      }
      if (!editMember) {
        addCompanyMember(data);
      } else {
        editCompanyMember(editMember.id, data);
      }

      event?.currentTarget.reset();
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        const newErrors = validationError.inner.reduce((acc, err) => {
          if (err.path) (acc as any)[err.path] = err.message;
          return acc;
        }, {} as Partial<Member>);
        setErrors(newErrors as Member);
      }
    }
  };

  const onFormChange = (event: FormEvent) => {
    const form = formReference.current
      ? new FormData(formReference.current)
      : new FormData();
    const data = Object.fromEntries(form.entries());
    const values = Object.values(data);
    const isEveryFieldFilled = values.every(Boolean);
    if (isEveryFieldFilled) {
      setAddButtonDisabled(false);
    } else {
      setAddButtonDisabled(true);
    }
  };

  const onEdit = (index: number) => {
    const member = addedMembers[index];
    setEditMember(member);
  };

  const onSaveButtonClick = () => {
    const form = formReference.current
      ? new FormData(formReference.current)
      : new FormData();
    const data = Object.fromEntries(form.entries());
    const values = Object.values(data);
    const isSomeFieldFilled = values.some(Boolean);
    if (isSomeFieldFilled) {
      onFormSubmit(undefined, form);
      setShouldFormClose(true);
    } else {
      nextStep();
      onClose();
    }
  };
  return (
    <Modal
      open={open}
      onClose={() => {
        if (
          status !== 'loading' ||
          deleteStatus !== 'loading' ||
          statusEdit! === 'loading'
        )
          onClose();
      }}
    >
      <Box className="absolute left-1/2 top-1/2 w-full max-w-[600px] -translate-x-1/2 -translate-y-1/2 transform">
        <Box className="mx-4 rounded-2xl bg-white px-6 py-8">
          <DeleteModal
            disabled={deleteStatus === 'loading'}
            title={'Are you sure to delete the User'}
            open={isConfirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={onConfirmDelete}
            className="w-full max-w-[600px]"
          />
          <Typography
            variant="h4"
            className="mb-2 text-center font-semibold text-black"
          >
            Adding a new User
          </Typography>
          <Box className="mt-9 flex max-h-[500px] flex-col items-center gap-5 overflow-y-auto overflow-x-hidden">
            {addedMembers.map((member, index) =>
              member.id === editMember?.id ? (
                <Box
                  component="form"
                  key={member.id}
                  className="w-full"
                  onSubmit={onFormSubmit}
                >
                  <ModalMemberItem
                    selectOptions={selectOptions}
                    index={index}
                    addedMember={editMember}
                    errors={errors}
                  />
                  <Button
                    className="mx-auto mt-9 min-w-48"
                    disabled={status === 'loading'}
                    type="submit"
                  >
                    <Typography>Save</Typography>
                  </Button>
                </Box>
              ) : (
                <AddedMemberItem
                  key={member.id}
                  member={member}
                  roleLabel={
                    selectOptions.find((option) => option.value === member.role)
                      ?.label || ''
                  }
                  index={index}
                  onEdit={() => onEdit(index)}
                  onDelete={() => onDelete(member.id)}
                />
              )
            )}
            <Box
              className="w-full"
              component="form"
              onSubmit={onFormSubmit}
              onChange={onFormChange}
              ref={formReference}
            >
              <ModalMemberItem
                resetForm={resetForm}
                selectOptions={selectOptions}
                index={addedMembers.length}
                errors={editMember ? ({} as Member) : errors}
              />
              <Button
                className="mx-auto mt-9"
                disabled={
                  !!editMember ||
                  status === 'loading' ||
                  deleteStatus === 'loading' ||
                  isAddButtonDisabled
                }
                type="submit"
              >
                <PlusIcon />
                <Typography>Add another user</Typography>
              </Button>
            </Box>
          </Box>
          <Box className="mt-8 flex justify-center gap-4">
            <Button
              variantType="outlined"
              className="min-w-[120px]"
              disabled={status === 'loading'}
              onClick={onClose}
            >
              <Typography>Cancel</Typography>
            </Button>
            <Button
              className="min-w-[120px]"
              onClick={onSaveButtonClick}
              disabled={
                status === 'loading' ||
                deleteStatus === 'loading' ||
                statusEdit === 'loading'
              }
            >
              <Typography>
                {addedMembers.length > 0 ? 'Next' : 'Save'}
              </Typography>
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
